import { useTheme } from "@emotion/react";
import type { AviaryColors } from "aviary-tokens";
import { colorProfileMapper } from "aviary-tokens";
import type { HTMLProps, ReactNode } from "react";

import type { Spacing } from "@aviary/types/spacing";

import type { ArrangeJustify } from "../../Arrange";

import * as styles from "./DeprecatedCell.styles";

interface DeprecatedCellProps extends HTMLProps<HTMLDivElement> {
  /**
   * Removes gutters made by padding from the Container component. Uses negative margin.
   *
   * @default tight
   */
  verticalPadding?: Spacing;
  /**
   * Removes default bottom margin from the cell
   *
   * @default false
   */
  isMarginless?: boolean;
  /**
   * Changes flex flow to be row instead of column
   *
   * @default false
   */
  isSingleRow?: boolean;
  /**
   * Adjust the horizontal alignment of elements
   */
  justify?: ArrangeJustify;
  /**
   * Specifies if there is a background color
   *
   * @default true
   */
  hasBackground?: boolean;
  /**
   * Specifies if Component will stretch to fill its container
   *
   * @default true
   */
  isFullWidth?: boolean;
  /**
   * Color intention
   */
  isColor?: Extract<AviaryColors, "success" | "system" | "highlight">;
  /**
   * Cell content
   *
   * @default undefined
   */
  children?: ReactNode;
}

const DeprecatedCell = ({
  verticalPadding = "tight",
  hasBackground = true,
  isMarginless,
  justify,
  isSingleRow,
  isColor = "system",
  isFullWidth = true,
  children,
  ...rest
}: DeprecatedCellProps) => {
  const currentTheme = useTheme();
  const themeColors = colorProfileMapper(currentTheme);

  const css = [
    styles.cell,
    styles.verticalPadding[verticalPadding],
    isMarginless && styles.marginless,
    isSingleRow && styles.flexRow,
    justify && styles.justify[justify],
    hasBackground && styles.background(themeColors[isColor]),
    !isFullWidth && styles.fitContent,
  ];

  return (
    <div css={css} {...rest}>
      {children}
    </div>
  );
};
export { DeprecatedCell };
